import { useMemo, useSyncExternalStore } from "react";
import { onValue, ref } from "firebase/database";
import { database } from "../constants/firebase";
import { useUser } from "../contexts/UserContext";
import { chatIdFor, inboxRows } from "../utils/messageInbox";
const stores = new Map();
function createStore(uid) {
  let state = {
    users: {},
    groups: {},
    threads: {},
    loading: Boolean(uid),
    error: "",
  };
  let roots = [];
  let timer;
  const threads = new Map();
  const listeners = new Set();
  let userReady = false,
    groupReady = false;
  const emit = () => listeners.forEach((fn) => fn());
  const patch = (next) => {
    state = { ...state, ...next, loading: !(userReady && groupReady) };
    emit();
  };
  const listenThread = (key, path) =>
    onValue(
      ref(database, path),
      (snapshot) =>
        patch({ threads: { ...state.threads, [key]: snapshot.val() || {} } }),
      () =>
        patch({
          error:
            "Some conversations could not be loaded. Try reopening Messages.",
        }),
    );
  const reconcile = () => {
    const wanted = new Map();
    Object.keys(state.users)
      .filter((id) => id !== uid)
      .forEach((id) =>
        wanted.set("dm:" + id, "privateChats/" + chatIdFor(uid, id)),
      );
    Object.entries(state.groups)
      .filter(([, g]) => g.hostId === uid || g.members?.[uid])
      .forEach(([id]) => wanted.set("group:" + id, "groupChats/" + id));
    for (const [key, unsubscribe] of threads)
      if (!wanted.has(key)) {
        unsubscribe();
        threads.delete(key);
        const next = { ...state.threads };
        delete next[key];
        state = { ...state, threads: next };
      }
    for (const [key, path] of wanted)
      if (!threads.has(key)) {
        // Reserve the slot before registering: cached Firebase snapshots may arrive immediately.
        threads.set(key, () => {});
        threads.set(key, listenThread(key, path));
      }
  };
  const start = () => {
    let hadTyping = false;
    const recentTyping = (value) =>
      value &&
      typeof value === "object" &&
      ((value.status && Date.now() - Number(value.timestamp || 0) < 4000) ||
        Object.values(value).some(recentTyping));
    timer = setInterval(() => {
      const active = Object.values(state.users).some((user) =>
        recentTyping(user.typingstatus),
      );
      if (active || hadTyping) patch({ clock: Date.now() });
      hadTyping = active;
    }, 2000);
    if (!uid) {
      userReady = true;
      groupReady = true;
      patch({ loading: false });
      return;
    }
    roots.push(
      onValue(
        ref(database, "Users"),
        (s) => {
          userReady = true;
          state = { ...state, users: s.val() || {} };
          reconcile();
          patch({});
        },
        () => {
          userReady = true;
          patch({ error: "Could not load your conversations." });
        },
      ),
    );
    roots.push(
      onValue(
        ref(database, "Groups"),
        (s) => {
          groupReady = true;
          state = { ...state, groups: s.val() || {} };
          reconcile();
          patch({});
        },
        () => {
          groupReady = true;
          patch({ error: "Could not load your groups." });
        },
      ),
    );
  };
  return {
    getSnapshot: () => state,
    subscribe: (fn) => {
      listeners.add(fn);
      if (listeners.size === 1) start();
      return () => {
        listeners.delete(fn);
        if (!listeners.size) {
          roots.forEach((off) => off());
          roots = [];
          clearInterval(timer);
          threads.forEach((off) => off());
          threads.clear();
          state = {
            users: {},
            groups: {},
            threads: {},
            loading: Boolean(uid),
            error: "",
          };
          userReady = false;
          groupReady = false;
        }
      };
    },
  };
}
export default function useMessageInbox() {
  const { currentUser } = useUser();
  const uid = currentUser?.uid || "";
  const store = useMemo(() => {
    if (!stores.has(uid)) stores.set(uid, createStore(uid));
    return stores.get(uid);
  }, [uid]);
  const state = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot,
  );
  const list = useMemo(
    () => inboxRows(state, currentUser),
    [state, currentUser],
  );
  return { ...state, list, me: state.users?.[uid] || currentUser || {} };
}
